import { HttpClient } from '../../../services/api/client'
import { deduplicateRequest } from '../../../services/api/deduplicateRequest'
import { TokenManager } from '../../../services/auth/tokenManager'
import type {
  ApiResponse,
  SurveySubmitResponse,
  SurveySimulateResponse,
  SurveyStatsResponse,
  SurveyResponsesResponse,
} from '@bandos/shared'
import type { SurveyQuestion, SurveyStatus } from '@bandos/shared'

export interface SurveyParticipantData {
  parentLabel: string
  surveyQuestions: SurveyQuestion[]
  surveyCreatedAt: string | null
  status: SurveyStatus
}

const dedupedGetStats = deduplicateRequest((nodeId: string) =>
  HttpClient.get<SurveyStatsResponse>(`/survey/${nodeId}/stats`)
)

export class SurveyService {
  /**
   * Fetch survey data for the public participant view (no auth required).
   */
  static async getPublicSurvey(nodeId: string): Promise<ApiResponse<SurveyParticipantData>> {
    return HttpClient.get<SurveyParticipantData>(`/survey/${nodeId}`)
  }

  /**
   * Fetch live response stats for a survey node.
   * Deduplicates concurrent requests for the same nodeId within a 2s window.
   */
  static getStats(nodeId: string): Promise<ApiResponse<SurveyStatsResponse>> {
    return dedupedGetStats(nodeId)
  }

  /**
   * Fetch all responses for a survey node (authenticated — creator only).
   */
  static async getResponses(nodeId: string): Promise<ApiResponse<SurveyResponsesResponse>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<SurveyResponsesResponse>(`/survey/${nodeId}/responses`, headers)
  }

  /**
   * Submit answers for a public survey (no auth required).
   * Answers must be ordered to match the survey's question indices.
   */
  static async submitAnswers(
    nodeId: string,
    answers: (string | number | boolean | string[] | null)[],
    durationSeconds?: number
  ): Promise<ApiResponse<SurveySubmitResponse>> {
    return HttpClient.post<SurveySubmitResponse>(`/survey/${nodeId}/responses`, {
      answers,
      durationSeconds,
    })
  }

  /**
   * Generate and insert 10 simulated participant responses (authenticated — creator only).
   */
  static async simulate(nodeId: string): Promise<ApiResponse<SurveySimulateResponse>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.post<SurveySimulateResponse>(`/survey/${nodeId}/simulate`, {}, headers)
  }
}
